/**
 * The one shape every action handler returns.
 *
 * @remarks
 * Tiny on purpose, and extracted in D53 so the container verbs could move to
 * their own module without importing their siblings: `action-handlers.ts` and
 * `container-handlers.ts` both need `completed` / `rejected`, and a helper
 * living in either one would make the other's import a cycle. A type and two
 * constructors are exactly the surface both sides share.
 *
 * The v0 policy that rejections never consume a turn is stated once, HERE,
 * rather than at each refusal site.
 *
 * @packageDocumentation
 */

import type { DefaultResponseSlot, NarrativeLine, RuntimeState } from '@orpheus/content-schema';

import type { ActionRejection, ActionRejectionReason } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { SpokenResponse } from '../defaults/default-responses.js';

import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { type StampedNarratives, stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/** Narrative material a handler may speak: an authored line, or a slot response that reports itself (D58). */
export type SpeakableLine = NarrativeLine | SpokenResponse;

/**
 * Split speakable material into its lines and the slots that spoke (D58).
 *
 * @remarks
 * A {@link SpokenResponse} is the only shape here carrying `slot`, so the
 * discrimination is structural. Authored lines contribute no slot — they are
 * fiction, not pool members, and bumping a count for them would advance pools
 * that never played.
 *
 * @param speakable - The mixed material
 * @returns The lines in order, and the slots that spoke
 */
export const splitSpokenLines = (
	speakable: readonly SpeakableLine[],
): { readonly lines: readonly NarrativeLine[]; readonly spokenSlots: readonly DefaultResponseSlot[] } => ({
	lines: speakable.map((item) => ('slot' in item ? item.line : item)),
	spokenSlots: speakable.flatMap((item) => ('slot' in item ? [item.slot] : [])),
});

/** A handler that ran to completion: emitted entries, advanced cursor, next state. */
export interface CompletedAction extends StampedNarratives {
	/** D58: slots this handler spoke — the executed arm's commit bumps their usage. */
	readonly spokenSlots?: readonly DefaultResponseSlot[];
	readonly state: RuntimeState;
	readonly type: 'completed';
}

/** How one action handler ended. */
export type ActionHandlerResult = CompletedAction | { readonly rejection: ActionRejection; readonly type: 'rejected' };

/**
 * Wrap a finished handler step.
 *
 * @param state - The state the handler produced
 * @param stamped - Its emitted entries and advanced cursor
 * @returns The completed result
 */
export const completed = (
	state: RuntimeState,
	stamped: StampedNarratives,
	spokenSlots: readonly DefaultResponseSlot[] = [],
): CompletedAction => ({
	cursor: stamped.cursor,
	entries: stamped.entries,
	...(spokenSlots.length === 0 ? {} : { spokenSlots }),
	state,
	type: 'completed',
});

/**
 * Build the rejected handler result (v0: rejections never consume a turn).
 *
 * @remarks
 * R15: the narratives RESOLVE here, at creation — a rejection changes no
 * state, so the world at this moment is the world at stamping time, and the
 * seam can hand out what the transcript will say (Calder's §5.2).
 *
 * @param bundle - The loaded bundle (conditional readings resolve against it)
 * @param state - The live runtime state, when the caller has one (absent, fallbacks speak)
 * @param reason - The typed reason
 * @param speakable - Authored lines and/or slot responses, in speaking order
 * @returns The rejected result
 */
export const rejected = (
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	reason: ActionRejectionReason,
	speakable: readonly SpeakableLine[],
	// R29: typed to the arm it always builds, so a caller that KNOWS it is
	// refusing (the reach gate) reads the rejection without a phantom branch.
): Extract<ActionHandlerResult, { type: 'rejected' }> => {
	const { lines, spokenSlots } = splitSpokenLines(speakable);

	return {
		rejection: {
			consumesTurn: false,
			narratives: resolveNarrativeLines(lines, bundle, state),
			reason,
			...(spokenSlots.length === 0 ? {} : { spokenSlots }),
		},
		type: 'rejected',
	};
};

/**
 * Complete a handler step that SPEAKS: stamp the material, record the slots.
 *
 * @remarks
 * The one construction every speaking handler shares, extracted so "stamp the
 * lines" and "record the slots that spoke" cannot be done separately — doing
 * them separately is precisely how a pool would silently hold at line one
 * (D31's dead-content rule, which D58 exists to close for handler slots).
 *
 * @param state - The state the handler produced
 * @param cursor - The turn's transcript cursor
 * @param speakable - Authored lines and/or slot responses, in speaking order
 * @param triggerContext - The entry's provenance stamp
 * @returns The completed result
 */
export const completedSpeaking = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	speakable: readonly SpeakableLine[],
	triggerContext: string,
): CompletedAction => {
	const { lines, spokenSlots } = splitSpokenLines(speakable);

	return completed(state, stampNarrativeLines(bundle, state, cursor, lines, triggerContext), spokenSlots);
};
